import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  Divider,
  Typography,
} from "@mui/material";
import {
  getProjectDetails,
  updateProjectDetail,
} from "../StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import { getProjectFilterMeta } from "../../../../MetaData/components/MetaList/components/ProjectFilterMeta/Service";
import CustomPageHeaderStyles from "../../../../../global/components/CustomPageHeader/CustomPageHeader.styles";
import YhRichTextEditor from "../../../../../ui/YhRichTextEditor";
import { YhButton, YhInput, YhMultiSelect, YhSelect } from "../../../../../ui";

interface CustomProps {
  nextStep?: () => void | undefined;
  backStep?: () => void | undefined;
  userId: String;
  idd: string;
  data?: any;
  fromSettingOrLogout?: any;
}

const tagOptions = [
  { label: "Hand picked", value: "Hand picked" },
  { label: "New Development", value: "New Development" },
  { label: "Top 10", value: "Top 10" },
];

const OptionCard = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex min-w-[110px] items-center justify-center rounded-xl border bg-white px-5 py-3 text-center text-sm font-medium transition ${
      selected
        ? "border-[#A02448] text-[#A02448] shadow-[0_0_0_1px_#A02448]"
        : "border-[#D4B8C0] text-[#A02448] hover:border-[#A02448]"
    }`}
  >
    <span className="leading-snug">{label}</span>
    {selected ? (
      <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#A02448] text-[10px] font-bold text-white">
        ✓
      </span>
    ) : null}
  </button>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-2 flex items-center gap-1.5">
    <span className="text-sm font-semibold text-[#111827]">{children}</span>
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#D1D5DB] text-[10px] text-[#9CA3AF]"
      title="More info"
    >
      i
    </span>
  </div>
);

const toId = (v: any) => {
  if (v == null) return "";
  if (typeof v === "object") return String(v._id || v.id || "");
  return String(v);
};

const OfficialInformation = (props: CustomProps) => {
  const [offialState, setOffialState] = useState<any>({
    specifications: [{ title: "", value: "" }],
    faqs: [{ question: "", answer: "" }],
    tags: [],
    amenities: [],
    selectedAmenities: [],
  });
  const [state, setState] = useState<any>({ amenities: {} });

  const setStateData = (newData: any) =>
    setState((prev: any) => ({ ...prev, ...newData }));

  const setOffialDetailState = (newData: any) =>
    setOffialState((prev: any) => ({ ...prev, ...newData }));

  const handleAddHighlightInfo = () => {
    setOffialDetailState({
      specifications: [
        ...(offialState.specifications || []),
        { title: "", value: "" },
      ],
    });
  };

  const handleAddFaq = () => {
    setOffialDetailState({
      faqs: [...(offialState.faqs || []), { question: "", answer: "" }],
    });
  };

  const handleChange = (index: number, field: string, value: any) => {
    const updated: any = [...(offialState.specifications || [])];
    updated[index] = { ...updated[index], [field]: value };
    setOffialDetailState({ specifications: updated });
  };

  useEffect(() => {
    projectDetails();
  }, [props?.idd]);

  useEffect(() => {
    init();
  }, []);

  const mapListingState = (data: any) => {
    if (!data) return;
    const amenityIds = (data.amenities || []).map(toId).filter(Boolean);
    const specs =
      Array.isArray(data.specifications) && data.specifications.length
        ? data.specifications
        : [{ title: "", value: "" }];
    const faqs =
      Array.isArray(data.faqs) && data.faqs.length
        ? data.faqs
        : [{ question: "", answer: "" }];
    const reraList = data?.rera?.project_rera || [];
    const reraId =
      (Array.isArray(reraList) && reraList[0]?.rera_id) ||
      data?.reraId ||
      (typeof data?.rera === "string" ? data.rera : "") ||
      "";

    setOffialDetailState({
      ...data,
      propertyPurpose: data.propertyPurpose || "",
      furnishingType: data.furnishingType || "",
      category: data.category || "",
      propertyStatus:
        data.propertyStatus || data.projectstatus_name || "",
      propertyAge: data.propertyAge ?? "",
      unitNo: data.unitNo ?? "",
      tags: Array.isArray(data.tags) ? data.tags.map(String) : [],
      amenities: amenityIds,
      selectedAmenities: amenityIds,
      specifications: specs,
      faqs,
      description: data.description || "",
      rera: {
        ...(data.rera || {}),
        project_rera: reraId
          ? [
              {
                ...(reraList[0] || {}),
                rera_id: reraId,
                project_name: reraList[0]?.project_name || "",
              },
            ]
          : reraList,
      },
    });
  };

  const projectDetails = async () => {
    if (props?.data) {
      mapListingState(props.data);
    }
    if (!props?.idd) return;
    try {
      const response = await getProjectDetails(props.idd);
      if (response?.data) mapListingState(response.data);
    } catch (error: any) {
      if (!props?.data) {
        openErrorNotification(
          typeof error?.message === "string"
            ? error.message
            : "Failed to load project listing details"
        );
      }
    }
  };

  const init = async () => {
    try {
      const filterActive = (list: any[] = []) =>
        list.filter((i) => i.status === true || i.status === 1 || i.status === "1");

      const projectFilterData = await getProjectFilterMeta();
      const response = projectFilterData?.data?.[0] || {};

      setStateData({
        propertyPurpose: filterActive(response.propertyPurpose),
        propertyStatus: filterActive(response.propertyStatus),
        categories: filterActive(response.categories),
        specifications: filterActive(response.specifications),
        furnishingType: filterActive(response.furnishingType),
        amenities: filterActive(response.amenities)?.reduce(
          (acc: any, amenity: any) => {
            const category = amenity.categoryName || "Other";
            if (!acc[category]) acc[category] = [];
            acc[category].push(amenity);
            return acc;
          },
          {}
        ),
      });
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Failed to load listing options from getProjectMeta"
      );
    }
  };

  const openAmenitiesModal = () => {
    setOffialDetailState({
      selectedAmenities: [...(offialState?.amenities || [])],
    });
    setStateData({ openTeamModal: true });
  };

  const submitForm = async () => {
    try {
      const amenityIds = (offialState.amenities || [])
        .map((item: any) => String(item?._id || item?.id || item || ""))
        .filter((id: string) => /^[a-f0-9]{24}$/i.test(id));
      const body = {
        _id: props?.idd,
        propertyPurpose: offialState.propertyPurpose || "",
        furnishingType: offialState.furnishingType || "",
        category: offialState.category || "",
        propertyStatus: offialState.propertyStatus || "",
        propertyAge: offialState.propertyAge ?? "",
        unitNo: offialState.unitNo ?? "",
        tags: Array.isArray(offialState.tags) ? offialState.tags : [],
        amenities: amenityIds,
        specifications: (offialState.specifications || []).map((item: any) => ({
          title: item?.title || "",
          value: item?.value || "",
        })),
        faqs: (offialState.faqs || [])
          .filter((item: any) => item?.question || item?.answer)
          .map((item: any) => ({
            question: item?.question || "",
            answer: item?.answer || "",
          })),
        description: offialState.description || "",
        rera: offialState.rera || { project_rera: [] },
      };
      if (props?.idd) {
        const response = await updateProjectDetail(body);
        if (response?.success) {
          openSuccessNotification(response?.message);
          props.nextStep?.();
        } else {
          openErrorNotification(response?.message || "Failed to save listing");
        }
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save listing");
    }
  };

  const purposeSelected = (item: any) =>
    offialState?.propertyPurpose === item.name ||
    offialState?.propertyPurpose === item._id;

  const furnishSelected = (item: any) =>
    offialState?.furnishingType === item._id ||
    offialState?.furnishingType === item.name;

  const propertyStatusValue = (() => {
    const current = offialState?.propertyStatus;
    if (!current) return "";
    const match = (state?.propertyStatus || []).find(
      (o: any) => o.name === current || String(o._id) === String(current)
    );
    return match ? String(match._id) : String(current);
  })();

  const reraValue =
    offialState?.rera?.project_rera?.[0]?.rera_id ||
    offialState?.reraId ||
    "";

  return (
    <div className="w-full">
      <div className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-sm font-bold text-[#A02448]">
          ☰
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-[#111827]">
            Project Listing
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Kindly confirm some facts about the property
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <FieldLabel>Property Purpose</FieldLabel>
          <div className="flex flex-wrap gap-3">
            {(state?.propertyPurpose || []).map((e: any) => (
              <OptionCard
                key={e._id}
                label={e.name}
                selected={purposeSelected(e)}
                onClick={() =>
                  setOffialDetailState({ propertyPurpose: e.name })
                }
              />
            ))}
          </div>
        </div>

        <div>
          <FieldLabel>Furnishing Type</FieldLabel>
          <div className="flex flex-wrap gap-3">
            {(state?.furnishingType || []).map((e: any) => (
              <OptionCard
                key={e._id}
                label={e.name}
                selected={furnishSelected(e)}
                onClick={() =>
                  setOffialDetailState({
                    furnishingType: e._id || e.name,
                  })
                }
              />
            ))}
          </div>
        </div>

        <div className="max-w-md">
          <YhSelect
            label="Project Status"
            placeholder="Select project status"
            searchPlaceholder="Search status..."
            showOptionIcon={false}
            clearable
            value={propertyStatusValue}
            options={(state?.propertyStatus || []).map((o: any) => ({
              label: o.name,
              value: String(o._id),
            }))}
            onChange={(e) => {
              const selected = (state?.propertyStatus || []).find(
                (o: any) => String(o._id) === String(e.target.value)
              );
              setOffialDetailState({
                propertyStatus: selected?.name || e.target.value || "",
              });
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <YhInput
            label="Property Age"
            placeholder="Enter Property Age"
            value={offialState?.propertyAge ?? ""}
            onChange={(e) =>
              setOffialDetailState({ propertyAge: e.target.value })
            }
          />
          <YhInput
            label="No. of Units"
            placeholder="Enter Unit No."
            value={offialState?.unitNo ?? ""}
            onChange={(e) => setOffialDetailState({ unitNo: e.target.value })}
          />
          <YhInput
            label="RERA Details"
            placeholder="Enter RERA ID"
            value={reraValue}
            onChange={(e) => {
              const updated = offialState.rera?.project_rera?.length
                ? [...offialState.rera.project_rera]
                : [{ rera_id: "", project_name: "" }];
              updated[0] = { ...updated[0], rera_id: e.target.value };
              setOffialDetailState({
                rera: { ...offialState.rera, project_rera: updated },
              });
            }}
          />
        </div>

        <YhMultiSelect
          label="Add Tags"
          placeholder="Select tags"
          searchPlaceholder="Search tags..."
          showOptionIcon={false}
          options={tagOptions}
          value={offialState?.tags || []}
          onChange={(values) =>
            setOffialDetailState({ tags: values.map(String) })
          }
        />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <FieldLabel>Amenities</FieldLabel>
            <YhButton variant="secondary" size="sm" onClick={openAmenitiesModal}>
              Add
            </YhButton>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(state.amenities || {}).map(
              ([category, amenities]: any) => {
                const selectedAmenities = offialState?.amenities || [];
                const selectedCount = amenities.filter((item: any) =>
                  selectedAmenities.includes(String(item._id))
                ).length;
                if (!selectedCount) return null;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={openAmenitiesModal}
                    className="rounded-full bg-[#EEF0FF] px-3 py-1 text-xs font-semibold text-[#A02448]"
                  >
                    {category} +{selectedCount}
                  </button>
                );
              }
            )}
            {!Object.values(state.amenities || {}).some((amenities: any) =>
              amenities.some((item: any) =>
                (offialState?.amenities || []).includes(String(item._id))
              )
            ) ? (
              <p className="text-sm text-[#9CA3AF]">No amenities selected</p>
            ) : null}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <FieldLabel>Specifications</FieldLabel>
            <YhButton
              variant="secondary"
              size="sm"
              onClick={handleAddHighlightInfo}
            >
              Add
            </YhButton>
          </div>
          <div className="space-y-3">
            {(offialState?.specifications || []).map(
              (item: any, index: number) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-3 md:grid-cols-2"
                >
                  <YhSelect
                    label="Key"
                    placeholder="Select"
                    showOptionIcon={false}
                    value={
                      state?.specifications?.find(
                        (val: any) =>
                          val.name === item.title ||
                          String(val._id) === String(item.key)
                      )?._id || ""
                    }
                    options={(state?.specifications || []).map((o: any) => ({
                      label: o.name,
                      value: String(o._id),
                    }))}
                    onChange={(e) => {
                      const opt = (state?.specifications || []).find(
                        (o: any) => String(o._id) === String(e.target.value)
                      );
                      const updated = [...(offialState.specifications || [])];
                      updated[index] = {
                        ...updated[index],
                        title: opt?.name ?? "",
                        key: opt?._id ?? "",
                      };
                      setOffialDetailState({ specifications: updated });
                    }}
                  />
                  <YhInput
                    label="Value"
                    placeholder="Enter"
                    value={item.value || ""}
                    onChange={(e) =>
                      handleChange(index, "value", e.target.value)
                    }
                  />
                </div>
              )
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <FieldLabel>Frequently Ask Question</FieldLabel>
            <YhButton variant="secondary" size="sm" onClick={handleAddFaq}>
              Add
            </YhButton>
          </div>
          <div className="space-y-3">
            {(offialState?.faqs || []).map((item: any, index: number) => (
              <div
                key={index}
                className="space-y-3 rounded-xl border border-[#EEF0F5] p-3"
              >
                <YhInput
                  label="Question"
                  placeholder="Question"
                  value={item.question ?? ""}
                  onChange={(e) => {
                    const updatedFaq = [...(offialState?.faqs || [])];
                    updatedFaq[index] = {
                      ...updatedFaq[index],
                      question: e.target.value,
                    };
                    setOffialDetailState({ faqs: updatedFaq });
                  }}
                />
                <YhInput
                  label="Answer"
                  placeholder="Enter"
                  multiline
                  rows={3}
                  value={item.answer ?? ""}
                  onChange={(e) => {
                    const updatedFaq = [...(offialState?.faqs || [])];
                    updatedFaq[index] = {
                      ...updatedFaq[index],
                      answer: e.target.value,
                    };
                    setOffialDetailState({ faqs: updatedFaq });
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <YhRichTextEditor
            value={offialState?.description ?? ""}
            onChange={(value: string) =>
              setOffialDetailState({ description: value })
            }
            style={{ minHeight: "200px", marginTop: "8px" }}
          />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EEF0F5] pt-5">
        <YhButton variant="secondary" onClick={() => props.backStep?.()}>
          ← Back
        </YhButton>
        <YhButton variant="primary" onClick={submitForm}>
          Save & Continue →
        </YhButton>
      </div>

      <Dialog
        open={!!state?.openTeamModal}
        onClose={() => setStateData({ openTeamModal: false })}
        maxWidth="sm"
        fullWidth
        style={{ maxWidth: "700px", margin: "0 auto" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={CustomPageHeaderStyles.heading} p={2}>
            Select Amenities
          </Typography>
          <Box display="flex" alignItems="center" pr={2} gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Select All
            </Typography>
            <Checkbox
              checked={
                Object.values(state.amenities || {})
                  .flat()
                  .every((item: any) =>
                    (offialState?.selectedAmenities || []).includes(
                      String(item._id)
                    )
                  ) &&
                Object.values(state.amenities || {}).flat().length > 0
              }
              onChange={() => {
                const allAmenities = Object.values(
                  state.amenities || {}
                ).flat() as any[];
                const allIds = allAmenities.map((item) => String(item._id));
                const selected = (
                  offialState?.selectedAmenities || []
                ).map(String);
                const allSelected = allIds.every((id) => selected.includes(id));
                const updated = allSelected
                  ? selected.filter((id: string) => !allIds.includes(id))
                  : Array.from(new Set([...selected, ...allIds]));
                setOffialDetailState({ selectedAmenities: updated });
              }}
              color="primary"
            />
          </Box>
        </Box>
        <Divider />
        <Box style={{ maxHeight: "500px", overflowY: "auto", padding: "8px" }}>
          <div className="space-y-4 p-2">
            {(Object.entries(state.amenities || {}) as [string, any[]][]).map(
              ([category, amenities]) => {
                const selectedAmenities = (
                  offialState?.selectedAmenities ||
                  offialState?.amenities ||
                  []
                ).map(String);
                return (
                  <div key={category}>
                    <p className="mb-2 text-sm font-semibold text-[#111827]">
                      {category}
                    </p>
                    {(amenities || []).map((e: any) => {
                      const id = String(e._id);
                      const isChecked = selectedAmenities.includes(id);
                      return (
                        <div
                          key={id}
                          className="mb-1 flex items-center justify-between"
                        >
                          <span className="text-sm text-[#374151]">
                            {e.name}
                          </span>
                          <Checkbox
                            checked={isChecked}
                            onChange={() => {
                              const updated = isChecked
                                ? selectedAmenities.filter(
                                    (x: string) => x !== id
                                  )
                                : [...selectedAmenities, id];
                              setOffialDetailState({
                                selectedAmenities: updated,
                              });
                            }}
                            color="primary"
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Box>
        <DialogActions>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => setStateData({ openTeamModal: false })}
          >
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={() => {
              setOffialDetailState({
                amenities:
                  offialState.selectedAmenities || offialState.amenities,
              });
              setStateData({ openTeamModal: false });
            }}
          >
            Submit
          </YhButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OfficialInformation;
